import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getMe } from "../features/authSlice";

import {
  FaArrowLeft,
  FaUpload,
  FaLeaf,
  FaUser,
  FaFileAlt,
  FaImage,
  FaTrash,
} from "react-icons/fa";

import { useStateContext } from "../contexts/ContextProvider";

const EditProgram = () => {
  const [judul, setJudul] = useState("");
  const [deskripsi, setDeskripsi] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageRemoved, setImageRemoved] = useState(false);

  const fileInputRef = useRef(null);
  const { currentColor, currentMode } = useStateContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { id } = useParams();

  // Colors
  const isDark = currentMode === "Dark";
  const accentColor = currentColor || "#c9862a";
  const bgColor = isDark ? "#1a1a18" : "#f7f3ec";
  const cardBg = isDark ? "#242420" : "#ffffff";
  const borderColor = isDark ? "#2e2e28" : "#e8e0d0";
  const textPrimary = isDark ? "#f0ece3" : "#1a1a18";
  const textSecondary = isDark ? "#a09880" : "#6b6456";
  const inputBg = isDark ? "#1a1a18" : "#ffffff";
  const inputBorder = isDark ? "#3a3a34" : "#e8e0d0";

  useEffect(() => {
    dispatch(getMe());
  }, [dispatch]);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      getProgramById()
    } else {
      navigate("/");
    }
  }, [navigate, user]);

  const getProgramById = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const apiUrl = process.env.REACT_APP_URL_API;
      const response = await axios.get(`${apiUrl}/program/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.data;
      setJudul(data.judul || "");
      setDeskripsi(data.deskripsi || "");
      setPreview(data.url || null);
    } catch (error) {
      console.error("Error fetching team data:", error);
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!judul.trim()) {
      newErrors.judul = "Judul wajib diisi";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const updateProgram = async (e) => {
    e.preventDefault();
    setError("");

    // Validate
    if (!validateForm()) {
      return;
    }

    const formData = new FormData();
    formData.append("judul", judul);
    formData.append("deskripsi", deskripsi);
    if (file) {
      formData.append("file", file);
    }
    formData.append("deleteImage", imageRemoved ? "true" : "false");

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("accessToken");
      const apiUrl = process.env.REACT_APP_URL_API;
      await axios.patch(`${apiUrl}/program/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      navigate("/list-program");

    } catch (error) {
      console.error("Error updating program:", error);
      if (error.response?.data?.msg) {
        setError(error.response.data.msg);
      } else {
        setError("Gagal memperbarui data program");
      }
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    processFile(selectedFile);
  };

  // Process file
  const processFile = (selectedFile) => {
    if (!selectedFile) return;

    setFile(selectedFile);
    setImageRemoved(false); // ada file baru, jadi bukan "hapus"

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(selectedFile);
  };

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  // Handle drag leave
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      processFile(droppedFile);
    }
  };

  // Handle remove image
  const handleRemoveImage = (e) => {
    if (e) e.stopPropagation();
    setFile(null);
    setPreview(null);
    setImageRemoved(true); // tandai user memang mau hapus gambar
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle click on upload area
  const handleUploadClick = (e) => {
    if (e) {
      e.stopPropagation();
    }
    fileInputRef.current?.click();
  };

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: bgColor }}>
      {/* Background Pattern */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 opacity-5">
          <FaLeaf
            className="absolute top-20 left-10 text-8xl"
            style={{ color: accentColor }}
          />
          <FaImage
            className="absolute bottom-20 right-20 text-7xl"
            style={{ color: accentColor }}
          />
        </div>
      </div>

      <div className="relative z-10 mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/list-program")}
            className="flex items-center gap-2 mb-4 transition-all duration-300 hover:opacity-80"
            style={{ color: textSecondary }}
          >
            <FaArrowLeft className="text-sm" />
            <span className="text-sm font-medium">
              Kembali ke Daftar Program
            </span>
          </button>

          <div
            className="p-6 rounded-xl border"
            style={{
              backgroundColor: cardBg,
              borderColor: borderColor,
            }}
          >
            <div className="flex items-center gap-4">
              <div
                className="p-3 rounded-xl"
                style={{ backgroundColor: accentColor }}
              >
                <FaUser className="text-white text-xl" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className="font-serif text-xs font-bold uppercase tracking-wider"
                    style={{ color: textSecondary }}
                  >
                    Program Management
                  </span>
                  <span className="text-xs" style={{ color: textSecondary }}>
                    —
                  </span>
                  <span
                    className="font-serif text-xs font-bold"
                    style={{ color: accentColor }}
                  >
                    Rumoh Pangan Aceh
                  </span>
                </div>
                <h1
                  className="text-2xl font-serif font-bold"
                  style={{ color: textPrimary }}
                >
                  Edit Program Baru
                </h1>
                <p className="text-sm mt-1" style={{ color: textSecondary }}>
                  Edit program baru untuk ditampilkan di website
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={updateProgram}>
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Image Upload */}
            <div className="lg:col-span-1">
              <div
                className="p-6 rounded-xl border sticky top-6"
                style={{
                  backgroundColor: cardBg,
                  borderColor: borderColor,
                }}
              >
                <h3
                  className="font-semibold mb-4 flex items-center gap-2"
                  style={{ color: textPrimary }}
                >
                  <FaImage className="text-sm" style={{ color: accentColor }} />
                  Logo
                </h3>

                {/* Image Upload Area */}
                <div
                  className="relative rounded-xl border-2 border-dashed transition-all duration-300 overflow-hidden cursor-pointer"
                  style={{
                    borderColor: isDragging ? accentColor : inputBorder,
                    backgroundColor: isDragging ? `${accentColor}15` : inputBg,
                  }}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={handleUploadClick}
                >
                  {preview ? (
                    <div className="relative">
                      <img
                        src={preview}
                        alt="Preview"
                        className="w-full h-64 object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-3">
                        <button
                          type="button"
                          onClick={handleUploadClick}
                          className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
                        >
                          <FaUpload className="text-white text-sm" />
                        </button>
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          className="p-2 rounded-lg bg-red-500/80 hover:bg-red-500 transition-colors"
                        >
                          <FaTrash className="text-white text-sm" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 px-4">
                      <div
                        className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                        style={{ backgroundColor: `${accentColor}20` }}
                      >
                        <FaUpload
                          className="text-2xl"
                          style={{ color: accentColor }}
                        />
                      </div>
                      <p
                        className="text-sm font-medium text-center mb-1"
                        style={{ color: textPrimary }}
                      >
                        Drag & Drop gambar di sini
                      </p>
                      <p
                        className="text-xs text-center"
                        style={{ color: textSecondary }}
                      >
                        atau klik untuk memilih file
                      </p>
                      <p
                        className="text-[10px] mt-3 px-3 py-1 rounded-full"
                        style={{
                          color: textSecondary,
                          backgroundColor: `${accentColor}15`,
                        }}
                      >
                        Format: JPG, PNG (Maks. 2MB)
                      </p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
              </div>
            </div>

            {/* Right Column - Form Fields */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Info Card */}
              <div
                className="p-6 rounded-xl border"
                style={{
                  backgroundColor: cardBg,
                  borderColor: borderColor,
                }}
              >
                <h3
                  className="font-semibold mb-6 flex items-center gap-2"
                  style={{ color: textPrimary }}
                >
                  <FaUser className="text-sm" style={{ color: accentColor }} />
                  Informasi Dasar
                </h3>

                {/* Nama */}
                <div className="mb-5">
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: textPrimary }}
                  >
                    Judul <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="judul"
                      value={judul}
                      onChange={(e) => setJudul(e.target.value)}
                      placeholder="Masukkan nama lengkap"
                      className="w-full px-4 py-3 pl-11 rounded-lg border transition-all duration-300 outline-none"
                      style={{
                        backgroundColor: inputBg,
                        borderColor: errors.judul ? "#ef4444" : inputBorder,
                        color: textPrimary,
                      }}
                      onFocus={(e) =>
                        (e.target.style.borderColor = accentColor)
                      }
                      onBlur={(e) =>
                        (e.target.style.borderColor = errors.judul
                          ? "#ef4444"
                          : inputBorder)
                      }
                    />
                    <FaUser
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-sm"
                      style={{ color: textSecondary }}
                    />
                  </div>
                  {errors.judul && (
                    <p className="text-xs mt-1" style={{ color: "#ef4444" }}>
                      {errors.judul}
                    </p>
                  )}
                </div>

                {/* Deskripsi */}
                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: textPrimary }}
                  >
                    Deskripsi / Bio
                  </label>
                  <div className="relative">
                    <textarea
                      name="deskripsi"
                      value={deskripsi}
                      onChange={(e) => setDeskripsi(e.target.value)}
                      placeholder="Tuliskan deskripsi singkat tentang program"
                      rows={5}
                      className="w-full px-4 py-3 pl-11 rounded-lg border transition-all duration-300 outline-none resize-none"
                      style={{
                        backgroundColor: inputBg,
                        borderColor: errors.deskripsi ? "#ef4444" : inputBorder,
                        color: textPrimary,
                      }}
                      onFocus={(e) =>
                        (e.target.style.borderColor = accentColor)
                      }
                      onBlur={(e) =>
                        (e.target.style.borderColor = errors.deskripsi
                          ? "#ef4444"
                          : inputBorder)
                      }
                    />
                    <FaFileAlt
                      className="absolute left-4 top-4 text-sm"
                      style={{ color: textSecondary }}
                    />
                  </div>
                  <div className="flex justify-between mt-2">
                    {errors.deskripsi && (
                      <p className="text-xs" style={{ color: "#ef4444" }}>
                        {errors.deskripsi}
                      </p>
                    )}
                    <p
                      className={`text-xs ml-auto ${deskripsi.length > 500 ? "text-red-500" : ""}`}
                      style={{ color: textSecondary }}
                    >
                      {deskripsi.length}/500 karakter
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div
                className="p-6 rounded-xl border flex flex-col sm:flex-row gap-4 justify-end"
                style={{
                  backgroundColor: cardBg,
                  borderColor: borderColor,
                }}
              >
                <button
                  type="button"
                  onClick={() => navigate("/list-program")}
                  disabled={isSubmitting}
                  className="flex-1 sm:flex-none px-6 py-3 rounded-xl font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: isDark ? "#1a1a18" : "#f7f3ec",
                    color: textPrimary,
                    border: `1px solid ${borderColor}`,
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = isDark
                      ? "#282C33"
                      : "#e8e0d0")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = isDark
                      ? "#1a1a18"
                      : "#f7f3ec")
                  }
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-8 py-3 rounded-xl font-medium text-white transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: accentColor,
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = "#b5772a")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = accentColor)
                  }
                >
                  <FaLeaf className="text-sm" />
                  {isSubmitting ? "Menyimpan..." : "Update Program"}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProgram;
